import { finalRequestScheduleToENG, finalScheduleToCSD, getRequestScheduleMailer } from "redux/crm/project/changeOrderSlice";
import EmployeeRecipientSelect from "components/common/select/EmployeeRecipientSelect";
import { Drawer, Button, Alert } from 'components/common';
import { toast } from "react-toastify";
import { useForm, Controller } from "react-hook-form";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import TinymceEditor from "components/common/TinymceEditor";
import DatePicker from "react-datepicker";
import { adjustToNoon } from 'helpers/utils';
import RequestScheduleForm from "./enter_job/final/RequestScheduleForm";
import useAuth from "hooks/useAuth";
import { FaBan, FaComment, FaInfoCircle, FaShareSquare } from "react-icons/fa";
import { MdEmail } from "react-icons/md";
import PropTypes from 'prop-types';
import { useNavigate } from "react-router-dom";
import { INPUT_CLS } from "helpers/formStyles";

const FORM_ID = "co-final-request-schedule-form";

const CHECKBOX_CLS = "tw-w-4 tw-h-4 tw-accent-blue-600 tw-cursor-pointer";
const FIELD_LABEL_CLS = "tw-w-16 tw-flex-shrink-0 tw-text-sm tw-text-slate-600";

const ChangeOrderFinalRequestSchedule = ({showModal, setshowModal, changeOrder, title, isScheduled }) => {
  const {control, register, handleSubmit, formState: {errors}, setValue} = useForm();
  const [documentSizeExceedMessage, setDocumentSizeExceedMessage] = useState("");
  const {userName} = useAuth();

  const [changeOrderMailer, setChangeOrderMailer] = useState({
    to_names: [],
    cc_names: [],
    subject: ''
  });
  const [showLastestCoDate, setShowLastestCoDate] = useState(false);

  const [estimateData, setEstimateData] = useState(null);
  const [abErectionData, setAbErectionData] = useState(null);
  const [apdData, setApdData] = useState([]);
  const [shopData, setShopData] = useState([]);
  const [otherData, setOtherData] = useState([]);

  const ppd_rad_documents = changeOrder?.ppd_rad_documents || [];
  const [selectedDocumentId, setSelectedDocumentId] = useState(ppd_rad_documents.map((document) => document.id) || []);

  const REQUEST_TYPE = isScheduled ? "schedule" : "request";

  const { project, building } = changeOrder;
  const projectId = project.id;
  const buildingId = building.id;
  const changeOrderId = changeOrder.id;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(getRequestScheduleMailer({
      projectId: projectId,
      buildingId: buildingId,
      changeOrderId: changeOrderId,
      params: { type: REQUEST_TYPE, drawing_type: "" },
    })).then((action) => {
      const { to_names, cc_names, subject } = action.payload;
      setChangeOrderMailer({ to_names, cc_names, subject });
    });
  }, [dispatch, changeOrder, REQUEST_TYPE, projectId, buildingId, changeOrderId]);


  useEffect(() => {
    const { to_names, cc_names, subject } = changeOrderMailer;
    setValue("to", to_names);
    setValue("cc", cc_names);
    setValue("subject", subject);
    setValue("comment", changeOrder.sales_note);

  }, [setValue, changeOrderMailer, changeOrder.sales_note]);

  useEffect(() => {
    const {estimate, parts, phases, ab_erection, others} = changeOrder

    if(estimate) {
      setEstimateData(estimate)
    }

    if(parts) {
      setApdData(parts)
    }

    if(phases) {
      setShopData(phases)
    }

    if(ab_erection) {
      setAbErectionData(ab_erection)
    }

    if(others) {
      setOtherData(others)
    }

  }, [changeOrder, setValue])

  const closeModal = () => {
    setshowModal(false)
  }

  const handleCancel = () => {
    closeModal()
  }

  const handleDocumentCheckboxChange = (documentId) => {
    // Get the updated selected documents array based on the current selection
    setSelectedDocumentId((prevSelectedIds) => {
      const updatedSelectedIds = prevSelectedIds.includes(documentId)
        ? prevSelectedIds.filter(id => id !== documentId)
        : [...prevSelectedIds, documentId];

      // Calculate the total size of the selected documents
      const totalSize = ppd_rad_documents
        .filter(doc => updatedSelectedIds.includes(doc.id))
        .reduce((sum, doc) => sum + doc.size, 0);

      // Check if the total size exceeds 20 MB
      if (totalSize > 20 * 1024 * 1024) {
        setDocumentSizeExceedMessage("The total attachment file size exceeds 20 MB. If you proceed, the system will not send the email with attachments.");
      } else {
        setDocumentSizeExceedMessage("");
      }

      return updatedSelectedIds;
    });
  };

  const onSubmit = (data) => {
    data = {
      ...data,
      estimate: isScheduled ? estimateData : {...estimateData, est_requested_sent: true, est_requested_by: userName},
      parts: apdData,
      phases: shopData,
      others: otherData,
      ab_erection: isScheduled ? abErectionData : {...abErectionData, requested_by: userName},
      document_ids: selectedDocumentId
    }

    try {
      const newData = {
        projectId: projectId,
        buildingId: buildingId,
        changeOrderId: changeOrderId,
        data
      }

      if (isScheduled) {
        dispatch(finalScheduleToCSD(newData))
      }
      else
      {
        dispatch(finalRequestScheduleToENG(newData))
      }

      closeModal();
      navigate(`/crm/projects/${projectId}/buildings/${buildingId}/change-orders/${changeOrderId}`);
    }
    catch(error) {
      toast.error(error.message)
    }
  }

  return (
    <Drawer
      show={showModal}
      onHide={closeModal}
      title={`${title}!`}
      placement="right"
      width="50%"
      backdropClick={false}
      footer={
        <>
          <Button type="submit" form={FORM_ID} variant="primary" size="sm" leftIcon={FaShareSquare}>
            {title}
          </Button>
          <Button variant="danger" size="sm" leftIcon={FaBan} onClick={handleCancel}>
            Cancel
          </Button>
        </>
      }
    >
      {documentSizeExceedMessage && (
        <Alert type="warning" className="tw-mb-4">
          {documentSizeExceedMessage}
        </Alert>
      )}

      <form id={FORM_ID} onSubmit={handleSubmit(onSubmit)}>
        <div className="tw-flex tw-items-center tw-gap-1.5 tw-mb-3 tw-text-sm tw-font-semibold tw-text-slate-700">
          <MdEmail className="tw-text-blue-600" size={15} /> Email:
        </div>

        {/* To */}
        <div className="tw-flex tw-items-start tw-gap-2 tw-mb-2">
          <span className={`${FIELD_LABEL_CLS} tw-mt-2`}>To</span>
          <div className="tw-flex-1 tw-min-w-0">
            <Controller
              control={control}
              name="to"
              rules={{required: "To must be required"}}
              render={({ field }) => (
                <EmployeeRecipientSelect
                  value={field.value}
                  onChange={field.onChange}
                  hasError={!!errors.to}
                  placeholder="Select..."
                />
              )}
            />
            {errors.to && (
              <p className="tw-text-xs tw-text-red-600 tw-mt-1 tw-mb-0">
                {errors.to.message}
              </p>
            )}
          </div>
        </div>

        {/* CC */}
        <div className="tw-flex tw-items-start tw-gap-2 tw-mb-2">
          <span className={`${FIELD_LABEL_CLS} tw-mt-2`}>CC</span>
          <div className="tw-flex-1 tw-min-w-0">
            <Controller
              control={control}
              name="cc"
              render={({ field }) => (
                <EmployeeRecipientSelect
                  value={field.value}
                  onChange={field.onChange}
                  hasError={!!errors.cc}
                  placeholder="Select..."
                />
              )}
            />
          </div>
        </div>

        {/* Subject */}
        <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
          <span className={FIELD_LABEL_CLS}>Subject</span>
          <div className="tw-flex-1 tw-min-w-0">
            <input
              type="text"
              placeholder="Subject"
              className={INPUT_CLS}
              {...register('subject', {required: true})}
            />
          </div>
        </div>

        {!isScheduled && (
          <>
            <div className="tw-my-5">
              <h6 className="tw-text-sm tw-font-semibold tw-text-slate-700 tw-mb-2">Attachments</h6>
              {
                ppd_rad_documents && ppd_rad_documents.length > 0 ? (
                  <div className="tw-rounded tw-border tw-border-dashed tw-border-slate-300 tw-p-3 tw-flex tw-flex-col tw-gap-2">
                    {ppd_rad_documents.map(document => (
                      <label
                        key={document.id}
                        className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-select-none tw-mb-0"
                      >
                        <input
                          type="checkbox"
                          name={document.id}
                          className={CHECKBOX_CLS}
                          onChange={() => handleDocumentCheckboxChange(document.id)}
                          checked={selectedDocumentId.includes(document.id)}
                        />
                        <span className="tw-text-sm tw-text-blue-600">
                          {document.name} ({(document.size / 1024).toFixed(2)} kb)
                        </span>
                        {document.revision_action === 'new' && (
                          <span className="tw-inline-flex tw-items-center tw-rounded-xl tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-bg-emerald-100 tw-text-emerald-700">
                            New{document.revision_rev != null ? ` · REV#${document.revision_rev}` : ''}
                          </span>
                        )}
                        {document.revision_action === 'replace' && (
                          <span className="tw-inline-flex tw-items-center tw-rounded-xl tw-px-2 tw-py-0.5 tw-text-[10px] tw-font-semibold tw-bg-amber-100 tw-text-amber-700">
                            Replaced{document.revision_rev != null ? ` · REV#${document.revision_rev}` : ''}
                          </span>
                        )}
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="tw-p-3 tw-text-sm tw-text-blue-600 tw-rounded tw-text-center tw-border tw-border-dashed tw-border-slate-300 tw-mb-3">
                    No attachment.
                  </div>
                )}
            </div>

            <div className="tw-mt-2 tw-flex tw-flex-col tw-gap-2">
              <div className="tw-flex tw-items-center tw-gap-3">
                <Controller
                  control={control}
                  name="lastest_co"
                  render={({ field }) => (
                    <label className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-select-none tw-mb-0">
                      <input
                        type="checkbox"
                        className={CHECKBOX_CLS}
                        checked={!!field.value}
                        onChange={(e) => {
                          field.onChange(e)
                          const checked = e.target.checked
                          setShowLastestCoDate(checked)
                          if (checked) {
                            setValue("lastest_co_date", adjustToNoon(new Date()))
                          }
                          else {
                            setValue("lastest_co_date", null)
                          }
                        }}
                      />
                      <span className="tw-text-sm tw-text-slate-700">Lastest CO# date</span>
                    </label>
                  )}
                />
                <Controller
                  control={control}
                  name="lastest_co_date"
                  //rules={{required: "Lastest CO# date must be required."}}
                  render={({ field }) => (
                    <DatePicker
                      selected={field.value}
                      onChange={d => field.onChange(adjustToNoon(d))}
                      className={INPUT_CLS}
                      placeholderText="dd-MMM-YYYY"
                      dateFormat="dd-MMM-yyyy"
                      disabled={!showLastestCoDate}
                      fixedHeight
                    />
                  )}
                />
              </div>

              <Controller
                control={control}
                name="always_include_schedule"
                render={({ field }) => (
                  <label className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-select-none tw-mb-0">
                    <input
                      type="checkbox"
                      className={CHECKBOX_CLS}
                      checked={!!field.value}
                      onChange={(e) => field.onChange(e)}
                    />
                    <span className="tw-text-sm tw-text-slate-700">Always included schedule</span>
                  </label>
                )}
              />
            </div>
          </>
        )}

        <h6 className="tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-semibold tw-text-blue-600 tw-mt-5 tw-mb-2">
          <FaInfoCircle size={13} /> Information:
        </h6>
        <RequestScheduleForm
          changeOrder={changeOrder}
          estimateData={estimateData}
          setEstimateData={setEstimateData}
          abErectionData={abErectionData}
          setAbErectionData={setAbErectionData}
          apdData={apdData}
          setApdData={setApdData}
          shopData={shopData}
          setShopData={setShopData}
          otherData={otherData}
          setOtherData={setOtherData}
          isScheduled={isScheduled}
        />

        <div className="tw-mt-8">
          <label className="tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-medium tw-text-slate-700 tw-mb-1.5">
            <FaComment className="tw-text-blue-600" size={12} /> Comment*
          </label>
          <Controller
            control={control}
            name="comment"
            rules={{required: "Comment must be required."}}
            render={({ field }) => (
              <TinymceEditor
                handleChange={field.onChange}
                value={field.value}
              />
            )}
          />
          {errors.comment && (
            <p className="tw-text-xs tw-text-red-600 tw-mt-1 tw-mb-0">
              {errors.comment.message}
            </p>
          )}
        </div>
      </form>
    </Drawer>
  )
};

ChangeOrderFinalRequestSchedule.propTypes = {
  showModal: PropTypes.bool.isRequired,
  setshowModal: PropTypes.func.isRequired,
  changeOrder: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  isScheduled: PropTypes.bool.isRequired
};

export default ChangeOrderFinalRequestSchedule;
